/* ──────────────────────────────────────────────────────────────────────
 *  ContributorWelcomeModal — the Explorer → Contributor promotion reveal.
 *
 *  Fires once when a user first becomes a Contributor (auto-promoted via the
 *  rung basics, or granted by invitation). Calm-celebratory: names how they
 *  got here, shows the Contribute tools that just unlocked, and teases the
 *  bigger Impact Signature moment that comes with their first contribution.
 *
 *  This is moment #1 of two. Moment #2 (the Impact Signature reveal + first
 *  Bronze) fires on the first contribution — a separate component.
 * ────────────────────────────────────────────────────────────────────── */

import React, { useEffect } from 'react';
import { PenLine, MessageCircle, Users, Hexagon, ArrowRight } from 'lucide-react';
import PromotionRing from './PromotionRing';
import styles from './ContributorWelcomeModal.module.css';

interface ContributorWelcomeModalProps {
  /** Display name — used for the avatar initials fallback only. Deliberately
   *  NOT used to address the user in copy (updated direction 2026-07-08:
   *  the headline is about the threshold crossed, not a salutation, which
   *  also avoids mis-addressing a user whose display value isn't a name). */
  displayName?: string;
  /** Profile photo for the ring animation (raw URL; PromotionRing resolves). */
  avatarUrl?: string | null;
  onClose: () => void;
  /** §7.16 beat 2 (chunk B slice D): "Show me what I can do" — the reveal
   *  path. Host closes AND navigates to the signature page, where the
   *  first-render reveal + ExplainRadarModal + rail-card materialization
   *  compose one arrival moment. Falls back to plain close when absent. */
  onShowMe?: () => void;
}

export default function ContributorWelcomeModal({ displayName, avatarUrl, onClose, onShowMe }: ContributorWelcomeModalProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    // No backdrop-click dismissal: this is a once-only moment, so a stray
    // click outside must not silently consume it. Esc + the buttons close.
    <div className={styles.backdrop} role="presentation">
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-label="Trust level advanced — you've earned your voice"
      >
        {/* The avatar + ring is the emotional centerpiece: the system
            recognizing the member. Variant E "heartbeat" animation ends on
            the persistent blue ring; the Contributor badge is pinned to the
            avatar's lower edge. (Updated design direction 2026-07-08.) */}
        <div className={styles.avatarStage}>
          <PromotionRing avatarUrl={avatarUrl} displayName={displayName} size={230} />
          <span className={styles.contributorBadge}>Contributor</span>
        </div>

        <div className={styles.eyebrow}>Trust level advanced</div>

        <div className={styles.step}>
          <span className={styles.from}>Explorer</span>
          <ArrowRight size={13} strokeWidth={2.2} aria-hidden="true" />
          <span className={styles.to}>Contributor</span>
        </div>

        <h2 className={styles.h}>You&rsquo;ve earned your voice.</h2>
        <p className={styles.sub}>
          You completed the basics, joined a community, and started following what matters to you.
          Now you can participate more fully in Life4Earth.
        </p>

        <div className={styles.cards}>
          <div className={styles.card}>
            <PenLine size={24} strokeWidth={1.9} aria-hidden="true" />
            <span className={styles.ct}>Post</span>
            <span className={styles.cd}>Share updates, stories, and useful knowledge.</span>
          </div>
          <div className={styles.card}>
            <MessageCircle size={24} strokeWidth={1.9} aria-hidden="true" />
            <span className={styles.ct}>Connect</span>
            <span className={styles.cd}>Comment, message, and join conversations.</span>
          </div>
          <div className={styles.card}>
            <Users size={24} strokeWidth={1.9} aria-hidden="true" />
            <span className={styles.ct}>Pitch in</span>
            <span className={styles.cd}>Volunteer for roles and contribute project data.</span>
          </div>
        </div>

        <div className={styles.teaser}>
          <Hexagon size={17} strokeWidth={2} aria-hidden="true" />
          <span>Your <strong>Impact Signature</strong> begins with what you contribute.</span>
        </div>

        <p className={styles.nopressure}>Use these when you&rsquo;re ready — nothing here is required.</p>

        <div className={styles.ctas}>
          <button className={styles.primary} onClick={onShowMe ?? onClose} type="button">
            Show me what I can do
            <ArrowRight size={16} strokeWidth={2.2} aria-hidden="true" />
          </button>
          <button className={styles.secondary} onClick={onClose} type="button">Later</button>
        </div>

        {/* Light Contributor→Champion boundary — prevents the misunderstanding
            that this rung includes building/leading, without burying the
            moment in governance logic. 2FA intentionally not mentioned here
            (not the immediate next step). */}
        <p className={styles.championNote}>
          Building projects comes later — <strong>Champion</strong> access unlocks projects,
          workspaces, teams, and approvals.
        </p>
      </div>
    </div>
  );
}
